import { Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { AppNotificationService } from '@core/services/app-notification.service';
import { Boat } from '@shared/models/boat';
import { Company } from '@shared/models/company';
import { Person } from '@shared/models/person';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslationService } from '@core/translation/translation.service';
import { isUp2boatId } from '@modules/bcm/@shared/services/up2boat-id-selection.service';

@UntilDestroy()
@Component({
    selector: 'select-boat-by-person-or-company',
    templateUrl: './select-boat-by-person-or-company.component.html',
    styleUrls: ['./select-boat-by-person-or-company.component.scss'],
    standalone: false
})
export class SelectBoatByPersonOrCompanyComponent {

    formGroupPrepare: UntypedFormGroup;

    formGroup: UntypedFormGroup;

    givenBoats: Boat[] = [];

    constructor(private _dialogRef: MatDialogRef<SelectBoatByPersonOrCompanyComponent>,
                private _appNotificationService: AppNotificationService,
                private _formBuilder: UntypedFormBuilder) {
        this._dialogRef.addPanelClass('default-dialog');
        this._dialogRef.disableClose = true;

        this.formGroupPrepare = this._formBuilder.group({
            type: ['all']
        });

        this.formGroup = this._formBuilder.group({});

        this.formGroupPrepare.get('type').valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(type => {
                if (type === 'all') {
                    this.givenBoats = [];
                }
            });

        this.formGroup.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(value => {
                if (value.personForm?.person?.id) {
                    this.givenBoats = value.personForm.person.boats;
                } else if (value.companyForm?.company?.id) {
                    this.givenBoats = value.companyForm.company.boats;
                }
            });
    }

    // Boats listed under the selected person/company carry no owner sub-graph —
    // they were listed *because* they belong to that owner, so attach it (consumers
    // default a booking's person from the boat's owner). Strictly limited to that
    // owner's own boats: the picker also offers the full catalog underneath, and a
    // boat taken from there has nothing to do with the selected person.
    private _withOwner(boat: Boat, person: Person, company: Company): Boat {
        const isOwnBoat = (this.givenBoats || []).some(({id}) => id === boat?.id);
        if (!boat?.id || !isOwnBoat || boat.owner?.id || boat.ownerCompany?.id) {
            return boat;
        }
        if (person?.id) {
            boat.owner = person;
        } else if (company?.id) {
            boat.ownerCompany = company;
        }
        return boat;
    }

    onClickClose(): void {
        this._dialogRef.close();
    }

    onClickSaveAndClose(): void {
        const boatValue = this.formGroup.get('boatForm')?.get('boat')?.value;
        const personValue = this.formGroup.get('personForm')?.get('person')?.value;

        if ((typeof boatValue === 'string' && isUp2boatId(boatValue))
            || (typeof personValue === 'string' && isUp2boatId(personValue))) {
            return;
        }

        if (this.formGroup.invalid || this.formGroupPrepare.invalid) {
            this.formGroup.markAllAsTouched();
            this.formGroupPrepare.markAllAsTouched();
            this._appNotificationService.showError(TranslationService.translate('common.checkFieldsHighlight'));
        } else {
            const {
                boatForm,
                personForm,
                companyForm,
            } = this.formGroup.value;

            this._dialogRef.close(
                this._withOwner(boatForm?.boat, personForm?.person, companyForm?.company),
            );
        }
    }
}
